import React, { useState } from "react";
import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LinkIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationIcon,
  ServerIcon,
  UsersIcon,
  GlobeIcon,
  CogIcon,
  DownloadIcon,
  BadgeCheckIcon,
  ClockIcon,
} from "@heroicons/react/solid";
import Gauge from "../Gauge/Gauge";

export default function ChronusAppDetail() {
  const [showTooltip, setShowTooltip] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Real Chronus Teams App data based on the provided manifest
  const appDetails = {
    name: "Chronus",
    developer: "Chronus",
    version: "1.1.1",
    manifestVersion: "1.11",
    id: "bb32d4e5-ae29-4c59-ba7e-a11611ca9710",
    officeAssetId: "WA200003051",
    mpnId: "6413776",
    description: "The Chronus mentoring platform empowers employees to connect, learn and grow. With Microsoft Teams for Chronus, receive instant updates on your mentoring program directly through your Teams app.",
    icon: "https://res.cdn.office.net/teamsappdata/ais_prod_v1/evergreen-assets/bb32d4e5-ae29-4c59-ba7e-a11611ca9710/2025082706583011/bb32d4e5-ae29-4c59-ba7e-a11611ca9710_largeimage.png",
    lastUpdated: "2022-03-11T13:06:35.068203+00:00",
    lastAnalyzed: "2024-01-20T16:45:00Z",
    overallScore: 78, // B+ grade (good with minor concerns)
    scoreBotSecurity: 88,
    scorePermissions: 55,
    scoreConfiguration: 92,
    scoreCompliance: 85,
    categories: ["HumanResourcesAndRecruiting", "Productivity", "TrainingAndTutorial", "Education"],
    industries: ["Education", "ProfessionalServices"],
    securityStatus: "Attested",
    isFullTrust: false,
    accentColor: "#FFFFFF"
  };

  const manifestRisks = [
    {
      category: "Valid Domains",
      finding: "Overly permissive wildcard domain patterns",
      severity: "High",
      riskScore: 8,
      description: "The app uses '*.*.chronus.com' which allows access to any subdomain at any level under chronus.com, including potentially untrusted or compromised subdomains.",
      manifestProperty: "validDomains: ['*.*.chronus.com', '*.chronus.com']",
      recommendation: "Replace wildcard patterns with specific, required subdomains. Maintain a whitelist of approved domain endpoints.",
      cweId: "CWE-923",
      realValue: "*.*.chronus.com, *.chronus.com, mentor.chronus.com"
    },
    {
      category: "Permissions",
      finding: "MessageTeamMembers permission granted",
      severity: "Medium",
      riskScore: 6,
      description: "The app can send direct messages to team members without explicit user consent for each message, which could be used for spam or phishing.",
      manifestProperty: "permissions: ['Identity', 'MessageTeamMembers']",
      recommendation: "Consider if automated messaging is essential. Implement user preference controls and message frequency limits.",
      cweId: "CWE-250",
      realValue: "Identity, MessageTeamMembers"
    },
    {
      category: "Bot Configuration",
      finding: "Limited bot scope - security positive",
      severity: "Low",
      riskScore: 2,
      description: "Bot is configured only for 'Personal' scope, which is more secure than broader team/channel access.",
      manifestProperty: "bots[0].scopes: ['Personal']",
      recommendation: "Good security practice. Continue limiting bot access to minimum required scope.",
      cweId: "N/A",
      realValue: "Personal scope only"
    },
    {
      category: "Static Tabs",
      finding: "Deep linking with user data in URLs",
      severity: "Medium",
      riskScore: 5,
      description: "Static tab contentUrl includes user email in URL parameters: '{userPrincipalName}' which could expose user data in logs.",
      manifestProperty: "staticTabs[1].contentUrl",
      recommendation: "Use POST requests or secure tokens instead of embedding user data in URL parameters.",
      cweId: "CWE-200",
      realValue: "https://mentor.chronus.com/ms_teams_chronus_tab?email={userPrincipalName}"
    },
    {
      category: "Manifest Version",
      finding: "Outdated manifest version",
      severity: "Medium",
      riskScore: 4,
      description: "App uses manifest version 1.11, while current version is 1.23. Missing latest security features and best practices.",
      manifestProperty: "manifestVersion: '1.11'",
      recommendation: "Update to manifest version 1.23 to leverage latest security features and compliance requirements.",
      cweId: "CWE-1104",
      realValue: "1.11 (current: 1.23)"
    },
    {
      category: "Security Compliance",
      finding: "Attested security status",
      severity: "Low",
      riskScore: 1,
      description: "App has 'Attested' security compliance status, indicating developer has completed security questionnaire.",
      manifestProperty: "securityComplianceInfo.status: 'Attested'",
      recommendation: "Maintain current attestation status and ensure regular compliance reviews.",
      cweId: "N/A",
      realValue: "Attested"
    }
  ];

  const permissions = [
    {
      name: "Identity",
      purpose: "Access user profile information",
      description: "Allows the app to access the signed-in user's identity including name, email, and profile picture for personalization",
      severity: "moderate",
      riskLevel: "Medium",
      justification: "Required for connecting users to their Chronus mentoring accounts"
    },
    {
      name: "MessageTeamMembers", 
      purpose: "Send notifications to team members",
      description: "Enables the app to send 1:1 messages to team members for mentoring program notifications and updates",
      severity: "dangerous",
      riskLevel: "High",
      justification: "Used for automated mentoring program notifications and meeting reminders"
    }
  ];

  const externalDomains = [
    {
      domain: "mentor.chronus.com",
      type: "Primary Service",
      risk: "Low",
      reason: "Main application domain for Chronus mentoring platform",
      recommendation: "Verify SSL/TLS configuration and security headers"
    },
    {
      domain: "*.chronus.com",
      type: "Wildcard Subdomain",
      risk: "Medium", 
      reason: "Allows any subdomain under chronus.com, could include development or staging environments",
      recommendation: "Replace with specific required subdomains"
    },
    {
      domain: "*.*.chronus.com",
      type: "Multi-level Wildcard",
      risk: "High",
      reason: "Extremely permissive pattern allowing any subdomain at any level",
      recommendation: "Remove this pattern and specify exact required domains"
    }
  ];

  const staticTabs = [
    {
      entityId: "conversations",
      name: "Conversations",
      scope: "Personal",
      risk: "Low",
      description: "Standard conversations tab"
    },
    {
      entityId: "chronusMentorApp", 
      name: "Chronus+",
      scope: "Personal",
      risk: "Medium",
      description: "Contains user email in URL parameters",
      contentUrl: "https://mentor.chronus.com/ms_teams_chronus_tab?from_teams_tab=true&email={userPrincipalName}&deep_link_url={subEntityId}"
    },
    {
      entityId: "about",
      name: "About",
      scope: "Personal", 
      risk: "Low",
      description: "Standard about page tab"
    }
  ];

  const complianceIssues = [
    {
      framework: "GDPR",
      issue: "User email exposed in URL parameters",
      severity: "Medium",
      description: "Personal data (email) included in URL may be logged by web servers and analytics tools"
    },
    {
      framework: "SOC 2",
      issue: "Manifest version compliance",
      severity: "Low", 
      description: "Using older manifest version may not meet current security controls requirements"
    },
    {
      framework: "ISO 27001",
      issue: "Overly broad domain access",
      severity: "Medium",
      description: "Wildcard domain patterns violate principle of least privilege"
    }
  ];

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  const getRiskColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getRiskBg = (severity) => {
    switch (severity.toLowerCase()) {
      case 'high': return 'bg-red-100';
      case 'medium': return 'bg-yellow-100'; 
      case 'low': return 'bg-green-100';
      default: return 'bg-gray-100';
    }
  };

  return (
    <>
      <div className="flex flex-col md:flex-row items-center justify-between">
        <div className="w-full md:w-1/3">
          <div className="flex items-center justify-start md:justify-start">
            <div className="w-1/4">
              <img
                src={appDetails.icon}
                className="rounded-2xl align-middle border-none max-w-full h-auto"
                alt="Chronus App Icon"
              />
            </div>
            <div className="w-3/4 pl-3">
              <h2 className="text-xl md:text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate flex items-center">
                <BadgeCheckIcon className="text-green-600 h-5 w-5 mr-2" />
                {appDetails.name}
              </h2>
              <h5 className="text-md md:text-lg leading-7 text-gray-900 sm:text-lg sm:truncate">
                {appDetails.developer}
              </h5>
              <h5 className="text-md md:text-lg leading-7 text-gray-900 sm:text-lg sm:truncate">
                Version {appDetails.version}
              </h5>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                <CheckCircleIcon className="w-3 h-3 mr-1" />
                {appDetails.securityStatus}
              </span>
            </div>
          </div>
        </div>

        <div className="w-full md:w-2/3 flex flex-wrap space-y-2 mt-4 md:mt-0">
          <div className="w-full flex justify-end text-right space-x-2">
            <span className="mb-2">
              <button
                type="button"
                className="text-white bg-[#2c4251] hover:bg-[#3d627a] font-medium rounded text-sm w-full sm:w-auto px-5 py-2.5 text-center uppercase"
              >
                <DownloadIcon
                  className="-ml-1 mr-2 h-5 w-5 inline"
                  aria-hidden="true"
                />
                Download Report
              </button>
            </span>
            <span className="mb-2">
              <a href="https://chronus.com" target="_blank" rel="noopener noreferrer">
                <button
                  type="button"
                  className="text-white bg-[#2c4251] hover:bg-[#3d627a] font-medium rounded text-sm w-full sm:w-auto px-5 py-2.5 text-center uppercase"
                >
                  <LinkIcon
                    className="-ml-1 mr-2 h-5 w-5 inline"
                    aria-hidden="true"
                  />
                  View Developer Site
                </button>
              </a>
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white overflow-hidden sm:rounded-lg mt-6 border-solid border-gray-200 border">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex mb-8">
            <div className="w-10/12">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-3">
                Chronus Teams Application Information
              </h3>
              <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <ServerIcon
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  Microsoft Teams App
                </div>
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <CogIcon
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  Manifest v{appDetails.manifestVersion}
                </div>
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <ClockIcon
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  Updated {new Date(appDetails.lastUpdated).toLocaleDateString("en-GB")}
                </div>
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <CalendarIcon
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  Analyzed {new Date(appDetails.lastAnalyzed).toLocaleDateString("en-GB")}
                </div>
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <UsersIcon
                    className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  Enterprise Mentoring
                </div>
              </div>
            </div>
            <div className="w-2/12 flex justify-end items-center h-auto">
              <div className="flex items-center flex-col relative">
                <div style={{ width: 130 }}>
                  <Gauge value={appDetails.overallScore} />
                </div>
                <div className="absolute top-0 right-0">
                  <InformationCircleIcon
                    className="h-5 w-5 text-gray-500 cursor-pointer"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  />
                  {showTooltip && (
                    <div
                      className="absolute top-0 right-full mt-1 p-2 bg-gray-100 rounded-md shadow-md text-sm"
                      style={{ minWidth: "500px" }}
                    >
                      <p className="text-sm text-gray-600">
                        <ul>
                          <li><strong>A*</strong> - Excellent security posture, minimal risks</li>
                          <li><strong>A</strong> - Good security with minor issues</li>
                          <li><strong>B</strong> - Acceptable security with some concerns</li>
                          <li><strong>C</strong> - Moderate security risks requiring attention</li>
                          <li><strong>D</strong> - Significant security issues requiring remediation</li>
                          <li><strong>F</strong> - Critical security vulnerabilities</li>
                        </ul>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Description</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {appDetails.description}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">App ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">
                  {appDetails.id}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Office Asset ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">
                  {appDetails.officeAssetId}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Categories</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {appDetails.categories.join(", ")}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Target Industries</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {appDetails.industries.join(", ")}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Security Score Breakdown */}
      <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg mt-6">
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-white px-4 py-5 sm:grid grid-cols-4 sm:gap-4 sm:px-6">
              <div className="flex justify-center sm:justify-start mb-4 sm:mb-0">
                <div className="text-sm text-gray-500 font-bold">
                  <div style={{ width: 170 }}>
                    <Gauge
                      value={appDetails.scorePermissions}
                      label="Permissions"
                      showVal={false}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-center sm:justify-start mb-4 sm:mb-0">
                <div className="text-sm text-gray-500 font-bold">
                  <div style={{ width: 170 }}>
                    <Gauge
                      value={appDetails.scoreBotSecurity}
                      label="Bot Security"
                      showVal={false}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-center sm:justify-start mb-4 sm:mb-0">
                <div className="text-sm text-gray-500 font-bold">
                  <div style={{ width: 170 }}>
                    <Gauge
                      value={appDetails.scoreConfiguration}
                      label="Configuration"
                      showVal={false}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-center sm:justify-start">
                <div className="text-sm text-gray-500 font-bold">
                  <div style={{ width: 170 }}>
                    <Gauge
                      value={appDetails.scoreCompliance}
                      label="Compliance"
                      showVal={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          </dl>
        </div>
      </div>

      {/* Static Tabs Analysis */}
      <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg mt-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Static Tabs Configuration
          </h3>
          <p className="text-sm text-gray-600">
            Analysis of static tabs configured in the Teams app and their security implications.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid grid-cols-5 sm:gap-4 sm:px-6">
              <dt className="text-sm text-gray-500 font-bold">Entity ID</dt>
              <dd className="text-sm text-gray-500 font-bold">Name</dd>
              <dd className="text-sm text-gray-500 font-bold">Scope</dd>
              <dd className="text-sm text-gray-500 font-bold">Risk</dd>
              <dd className="text-sm text-gray-500 font-bold">Notes</dd>
            </div>

            {staticTabs.map((tab, index) => (
              <div key={index} className={classNames(
                getRiskBg(tab.risk),
                "px-4 py-5 sm:grid grid-cols-5 sm:gap-4 sm:px-6"
              )}>
                <dt className="text-sm font-mono text-gray-900">{tab.entityId}</dt>
                <dd className="text-sm text-gray-700">{tab.name || "N/A"}</dd>
                <dd className="text-sm text-gray-700">{tab.scope}</dd>
                <dd className={classNames(getRiskColor(tab.risk), "text-sm font-bold")}>
                  {tab.risk}
                </dd>
                <dd className="text-sm text-gray-700">
                  {tab.description}
                  {tab.contentUrl && (
                    <div className="mt-1">
                      <code className="text-xs bg-gray-200 px-1 rounded">
                        {tab.contentUrl.substring(0, 60)}...
                      </code>
                    </div>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* External Domains */}
      <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg mt-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Valid Domains Analysis
          </h3>
          <p className="text-sm text-gray-600">
            Domains that the Chronus Teams app is configured to interact with, including risk assessment.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid grid-cols-5 sm:gap-4 sm:px-6">
              <dt className="text-sm text-gray-500 font-bold">Domain</dt>
              <dd className="text-sm text-gray-500 font-bold">Type</dd>
              <dd className="text-sm text-gray-500 font-bold">Risk</dd>
              <dd className="text-sm text-gray-500 font-bold col-span-2">Assessment</dd>
            </div>

            {externalDomains.map((domain, index) => (
              <div key={index} className={classNames(
                getRiskBg(domain.risk),
                "px-4 py-5 sm:grid grid-cols-5 sm:gap-4 sm:px-6"
              )}>
                <dt className="text-sm font-mono text-gray-900">
                  <GlobeIcon className="inline h-4 w-4 mr-1" />
                  {domain.domain}
                </dt>
                <dd className="text-sm text-gray-700">{domain.type}</dd>
                <dd className={classNames(getRiskColor(domain.risk), "text-sm font-bold")}>
                  {domain.risk}
                </dd>
                <dd className="text-sm text-gray-700 col-span-2">
                  <p><strong>Risk:</strong> {domain.reason}</p>
                  <p><strong>Recommendation:</strong> {domain.recommendation}</p>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Security Findings */}
      <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg mt-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Security Findings - Real Manifest Analysis
          </h3>
          <p className="text-sm text-gray-600">
            Detailed security analysis of the actual Chronus Teams app manifest configuration.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid grid-cols-4 sm:gap-4 sm:px-6">
              <dt className="text-sm text-gray-500 font-bold">Finding</dt>
              <dt className="text-sm text-gray-500 font-bold">Severity</dt>
              <dt className="text-sm text-gray-500 font-bold">Details</dt>
              <dt className="text-sm text-gray-500 font-bold">Actions</dt>
            </div>

            {manifestRisks.map((risk, index) => (
              <div key={index}>
                <div className={classNames(
                  risk.severity === "High" ? "bg-red-50" : 
                  risk.severity === "Medium" ? "bg-yellow-50" : "bg-green-50",
                  "px-4 py-5 sm:grid grid-cols-4 sm:gap-4 sm:px-6 relative"
                )}>
                  <div className="text-sm">
                    <span className="font-bold text-gray-900 block">{risk.finding}</span>
                    <span className="text-xs text-gray-500 block mt-1">
                      Category: {risk.category}
                    </span>
                    {risk.cweId !== "N/A" && (
                      <span className="text-xs text-gray-500 block">
                        CWE-ID: {risk.cweId}
                      </span>
                    )}
                  </div>
                  
                  <div className="text-center">
                    <span className={classNames(
                      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                      risk.severity === "High" ? "bg-red-100 text-red-800" :
                      risk.severity === "Medium" ? "bg-yellow-100 text-yellow-800" :
                      "bg-green-100 text-green-800"
                    )}>
                      {risk.severity === "High" && <ExclamationIcon className="w-4 h-4 mr-1" />}
                      {risk.severity}
                    </span>
                    <div className="text-xs text-gray-500 mt-1">
                      Risk Score: {risk.riskScore}/10
                    </div>
                  </div>

                  <div className="text-sm text-gray-700">
                    <p className="mb-2">{risk.description}</p>
                    <p className="text-xs text-gray-500 mb-2">
                      <strong>Actual Value:</strong> <code className="bg-gray-100 px-1 rounded">{risk.realValue}</code>
                    </p>
                    <p className="text-xs">
                      <strong>Recommendation:</strong> {risk.recommendation}
                    </p>
                  </div>

                  <div className="flex flex-col space-y-2">
                    <button className="text-white bg-green-500 hover:bg-green-600 font-medium rounded text-xs px-3 py-1">
                      ✓ Accept Risk
                    </button>
                    <button className="text-white bg-amber-500 hover:bg-amber-600 font-medium rounded text-xs px-3 py-1">
                      ⚠ Mitigate
                    </button>
                    <button className="text-white bg-red-500 hover:bg-red-600 font-medium rounded text-xs px-3 py-1">
                      ✗ Reject
                    </button>
                  </div>

                  <button
                    onClick={() => toggleRow(`risk-${index}`)}
                    className="absolute right-4 top-4"
                  >
                    {expandedRows[`risk-${index}`] ? (
                      <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                </div>
                
                {expandedRows[`risk-${index}`] && (
                  <div className="bg-gray-100 px-4 py-5 sm:px-6">
                    <h4 className="text-sm font-bold mb-3">Detailed Analysis</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <h5 className="font-bold mb-1">Manifest Property:</h5>
                        <code className="bg-white px-2 py-1 rounded block text-xs">
                          {risk.manifestProperty}
                        </code>
                      </div>
                      <div>
                        <h5 className="font-bold mb-1">Impact Assessment:</h5>
                        <p className="text-gray-700">
                          Risk score of {risk.riskScore}/10 indicates {risk.severity.toLowerCase()} priority for remediation.
                        </p>
                      </div>
                    </div>
                    <div className="mt-4">
                      <textarea
                        className="w-full rounded p-2 focus:border-mobstr-purple"
                        placeholder="Add detailed assessment notes and remediation plan..."
                        rows="3"
                        style={{ resize: "vertical" }}
                      />
                      <div className="mt-2 flex justify-end">
                        <button className="bg-mobstr-purple hover:bg-[#3d627a] text-white py-2 px-4 rounded">
                          Save Assessment
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Real Permissions Analysis */}
      <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg mt-6" id="permissions">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Teams Permissions Analysis
          </h3>
          <p className="text-sm text-gray-600">
            Analysis of actual permissions requested by the Chronus Teams app.
          </p>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid grid-cols-5 sm:gap-4 sm:px-6">
              <dt className="text-sm text-gray-500 font-bold">Permission</dt>
              <dd className="text-sm text-gray-500 font-bold">Purpose</dd>
              <dd className="text-sm text-gray-500 font-bold col-span-2">Description</dd>
              <dd className="text-sm text-gray-500 font-bold">Risk Level</dd>
            </div>

            {permissions.map((perm, index) => (
              <div key={index}>
                <div
                  className={classNames(
                    perm.riskLevel === "High" ? "bg-rose-200" : "bg-white",
                    "px-4 py-5 sm:grid grid-cols-5 sm:gap-4 sm:px-6 relative"
                  )}
                >
                  <dt className="text-xs text-gray-500 font-bold font-mono">
                    {perm.name}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0">
                    {perm.purpose}
                  </dd>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 col-span-2">
                    {perm.description}
                  </dd>
                  <dd className={classNames(
                    getRiskColor(perm.riskLevel),
                    "mt-1 text-sm font-bold sm:mt-0"
                  )}>
                    {perm.riskLevel}
                  </dd>
                </div>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white border-solid border-gray-200 border overflow-hidden sm:rounded-lg mt-6">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Chronus Security Assessment Summary
          </h3>
          <div className="prose max-w-none">
            <p className="text-sm text-gray-700">
              <strong>Overall Assessment:</strong> The Chronus Teams application demonstrates good security practices 
              in most areas but has some concerning configuration issues that should be addressed. The app benefits 
              from having "Attested" security compliance status and limited bot scope.
            </p>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-bold text-red-800 mb-2">High Priority Actions:</h4>
                <ul className="text-sm text-red-700 list-disc list-inside">
                  <li>Replace "*.*.chronus.com" wildcard with specific domains</li>
                  <li>Remove user email from URL parameters in static tabs</li>
                  <li>Update to latest manifest version (1.23)</li>
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-bold text-yellow-800 mb-2">Medium Priority Actions:</h4>
                <ul className="text-sm text-yellow-700 list-disc list-inside">
                  <li>Review necessity of MessageTeamMembers permission</li>
                  <li>Implement user preference controls for messaging</li>
                  <li>Add detailed access logging and monitoring</li>
                </ul>
              </div>
            </div>

            <div className="mt-4 bg-green-50 p-4 rounded-lg">
              <h4 className="font-bold text-green-800 mb-2">Positive Security Practices:</h4>
              <ul className="text-sm text-green-700 list-disc list-inside">
                <li>Bot scope limited to "Personal" only</li>
                <li>Security compliance status is "Attested"</li>
                <li>isFullTrust set to false</li>
                <li>Clear privacy and terms of use policies</li>
              </ul>
            </div>

            <div className="mt-4 bg-blue-50 p-4 rounded-lg">
              <h4 className="font-bold text-blue-800 mb-2">Deployment Recommendation:</h4>
              <p className="text-sm text-blue-700">
                This application can be deployed with medium confidence after addressing the high-priority wildcard 
                domain issues. The app shows good security fundamentals but would benefit from modernization to the 
                latest manifest version and improved data handling practices.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
